// DanceSpinners - Tanz-Rhythmus-Spinner
// N-1-2-3 Sequenz mit expandierenden Ringen oder ohne Ringe
// Mit 2-Sekunden-Anzeige aller Elemente vor Animation-Start

import { useEffect, useState } from "react";

// Gemeinsame Konstanten
const DANCE_LABELS = ["N", "1", "2", "3"];
const DANCE_POSITIONS = [
  { left: "20%", top: "50%" }, // N - links
  { left: "40%", top: "50%" }, // 1 - links-mitte
  { left: "60%", top: "50%" }, // 2 - rechts-mitte
  { left: "80%", top: "50%" }, // 3 - rechts
];
const DANCE_DELAYS = [0, 0.5, 1.5, 2.5]; // N→1: 0.5s, 1→2: 1s, 2→3: 1s, 3→N: 0.5s
const INITIAL_DISPLAY_DURATION = 2000; // 2 Sekunden alle Elemente anzeigen

// Größen-Konfiguration
const getSizeClasses = (size: "small" | "medium" | "large") =>
  ({
    small: "w-8 h-8",
    medium: "w-16 h-16",
    large: "w-32 h-32",
  })[size];

// Animation-Klassen basierend auf Label
const getAnimationClass = (label: string) => {
  if (label === "3") return "animate-count-pop-super-fast";
  if (label === "N") return "animate-count-pop-fast";
  return "animate-count-pop";
};

// Tanz-Rhythmus Spinner mit Ringen und 2-Sekunden-Anzeige
export const CoupleUpDanceSpinner = ({
  size = "large",
}: {
  size?: "small" | "medium" | "large";
}) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    // Nach 2 Sekunden Animation starten
    const timer = setTimeout(() => {
      setShowAnimation(true);
    }, INITIAL_DISPLAY_DURATION);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative ${getSizeClasses(size)} group`}>
      {DANCE_LABELS.map((label, idx) => (
        <div key={label} className="absolute inset-0">
          {/* Ring */}
          <div
            className="absolute rounded-full border-2 border-orange-400/30 animate-rhythm-ring"
            style={{
              width: "60px",
              height: "60px",
              left: DANCE_POSITIONS[idx].left,
              top: DANCE_POSITIONS[idx].top,
              transform: "translate(-50%, -50%)",
              animationDelay: showAnimation ? `${DANCE_DELAYS[idx]}s` : "0s",
              animationDuration: "3.0s",
            }}
          />
          {/* Zählung */}
          <div
            className={`absolute select-none font-bold text-orange-400 text-3xl drop-shadow-lg ${getAnimationClass(label)}`}
            style={{
              left: DANCE_POSITIONS[idx].left,
              top: DANCE_POSITIONS[idx].top,
              transform: "translate(-50%, -50%)",
              animationDelay: showAnimation ? `${DANCE_DELAYS[idx]}s` : "0s",
              animationDuration: "3.0s",
            }}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

// Kompakter Tanz-Rhythmus-Spinner ohne Ringe mit 2-Sekunden-Anzeige
export const CoupleUpCompactDanceSpinner = ({
  size = "large",
}: {
  size?: "small" | "medium" | "large";
}) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    // Nach 2 Sekunden Animation starten
    const timer = setTimeout(() => {
      setShowAnimation(true);
    }, INITIAL_DISPLAY_DURATION);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative ${getSizeClasses(size)} group`}>
      {DANCE_LABELS.map((label, idx) => (
        <div key={label} className="absolute inset-0">
          {/* Nur Zählung - keine Ringe */}
          <div
            className={`absolute select-none font-bold text-orange-400 text-3xl drop-shadow-lg ${getAnimationClass(label)}`}
            style={{
              left: DANCE_POSITIONS[idx].left,
              top: DANCE_POSITIONS[idx].top,
              transform: "translate(-50%, -50%)",
              animationDelay: showAnimation ? `${DANCE_DELAYS[idx]}s` : "0s",
              animationDuration: "3.0s",
            }}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

// HUSTLE-Spinner: & = N, HU = 1, ST = 2, LE = 3
// Mit Ringen (gleich fett) und erstem Symbol in halb so großer Schrift
// Vereinfachte, gleichmäßigere Abstände für bessere Lesbarkeit
// (soll ungefähr das Wort HUSTLE ergeben, ohne dass sich die Gruppen stark überlagern)
const HUSTLE_POSITIONS = [
  { left: "0%", top: "50%" }, // &
  { left: "35%", top: "50%" }, // HU
  { left: "70%", top: "50%" }, // ST
  { left: "100%", top: "50%" }, // LE
];

export const CoupleUpHustleSpinner = ({
  size = "large",
}: {
  size?: "small" | "medium" | "large";
}) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    // Nach 2 Sekunden Animation starten
    const timer = setTimeout(() => {
      setShowAnimation(true);
    }, INITIAL_DISPLAY_DURATION);

    return () => clearTimeout(timer);
  }, []);

  // HUSTLE Labels: &, HU, ST, LE
  const HUSTLE_LABELS = ["&", "HU", "ST", "LE"];

  // Textgröße: erstes Symbol (&) ist halb so groß
  const getTextSize = (label: string) => {
    if (label === "&") return "text-xl"; // Halb so groß
    return "text-3xl"; // Normale Größe
  };

  return (
    <div className={`relative ${getSizeClasses(size)} group`}>
      {HUSTLE_LABELS.map((label, idx) => (
        <div key={label} className="absolute inset-0">
          {/* Ring - alle gleich fett (border-2) */}
          <div
            className="absolute rounded-full border-2 border-orange-400/30 animate-rhythm-ring"
            style={{
              width: "60px",
              height: "60px",
              left: HUSTLE_POSITIONS[idx].left,
              top: HUSTLE_POSITIONS[idx].top,
              transform: "translate(-50%, -50%)",
              animationDelay: showAnimation ? `${DANCE_DELAYS[idx]}s` : "0s",
              animationDuration: "3.0s",
            }}
          />
          {/* Text - H ist halb so groß */}
          <div
            className={`absolute select-none font-bold text-orange-400 drop-shadow-lg ${getTextSize(label)} ${getAnimationClass(DANCE_LABELS[idx])}`}
            style={{
              left: HUSTLE_POSITIONS[idx].left,
              top: HUSTLE_POSITIONS[idx].top,
              transform: "translate(-50%, -50%)",
              animationDelay: showAnimation ? `${DANCE_DELAYS[idx]}s` : "0s",
              animationDuration: "3.0s",
              // Etwas Laufweite für Zweier-Gruppen (HU, ST, LE), damit sie lesbarer sind
              letterSpacing: label.length > 1 ? "0.08em" : undefined,
            }}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

// CoupleUp Login/Logout-Spinner
// Gleichmäßiges Flashen ohne rhythmische Taktung
// Für Login (Dashboard-Vorbereitung) und Logout
// Verwendet denselben Schriftzug wie im Dashboard-Header ("CoupleUp")
const COUPLEUP_TEXT = "CoupleUp";

export const CoupleUpLoginSpinner = ({
  size = "large",
}: {
  size?: "small" | "medium" | "large";
}) => {
  const textSizeClass =
    size === "large"
      ? "text-3xl"
      : size === "medium"
        ? "text-2xl"
        : "text-xl";

  // Kein festes w/h-„Portal“: leere relative Box + absolute Text wirkte wie ein extra Panel.
  return (
    <div
      className="inline-flex max-w-full items-center justify-center bg-transparent"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        className={`select-none font-bold text-orange-400 drop-shadow-lg animate-login-flash ${textSizeClass}`}
      >
        {COUPLEUP_TEXT}
      </span>
    </div>
  );
};
